import { Component } from 'react';

import type { ReactNode } from 'react';
import type { IErrorBoundary, IErrorBoundaryState } from './interfaces/IErrorBoundary';

/**
 * Keeps a render error inside one part of the page.
 *
 * Without a boundary, an error thrown while rendering unmounts the whole tree. With one, only the
 * children are replaced, by whatever `fallback` renders. `retry` mounts the children afresh, which
 * is the right move when the error came from one bad piece of data rather than from the code.
 *
 * A class on purpose: React has no hook for catching render errors. The error itself is not
 * logged here, since React already reports every error a boundary catches.
 */
class ErrorBoundary extends Component<IErrorBoundary, IErrorBoundaryState> {
  constructor(props: IErrorBoundary) {
    super(props);

    this.state = { hasError: false };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(): IErrorBoundaryState {
    return { hasError: true };
  }

  retry(): void {
    this.setState({ hasError: false });
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    return hasError ? fallback(this.retry) : children;
  }
}

export default ErrorBoundary;
